import type {
  BodyRegion,
  INJURY_AGES,
  INJURY_MOMENTS,
  INJURY_NATURES,
  INJURY_STATUSES,
  INJURY_TYPES,
  InjuryInput,
  InjuryOccurrence,
  PainTrend,
} from "@repo/contract/training";
import { type FieldErrors, NO_ERRORS } from "./validate";

/**
 * The pain editor's pure half: A2's `PainSheet` (editors.tsx:941-1085) mapped
 * onto the contract's injury report (injuries.ts).
 *
 * ── A2'S REGIONS ARE DISPLAY WORDS; THE CONTRACT'S ARE SEVENTEEN IDS ────────
 * A2 asked for a region ("Ombro") and then a side. The contract lists paired
 * joints left and right separately (`shoulder_r`, `shoulder_l`), so an area
 * plus a side is one region, and "Os dois" is TWO reports. The midline areas
 * (neck, upper and lower back, chest, abdomen) have no side. The contract's
 * three regions A2 did not offer (thoracic, chest, abdomen) are added, or a
 * stored report on them could not be edited.
 *
 * ── THE OTHER OCCURRENCE'S FIELDS ARE `null`, ALWAYS ────────────────────────
 * A current pain carries nature, intensity and when; a historical injury
 * carries type, status and how long ago; and the contract refuses a report
 * with the other branch populated (injuries.ts:113-131). The form keeps both
 * branches' answers while the person flips between them, and
 * {@link injuriesFromPainForm} writes only the chosen one.
 *
 * A2's free-text "Como é a dor?" is the report's `note` (T29); its "Desde
 * quando?" and "Como está evoluindo?" are `since` and `trend` (T59, T62).
 *
 * ── "DESDE QUANDO?" IS FOUR BUCKETS ON SCREEN AND A DAY ON THE WIRE ─────────
 * Every save RE-CREATES the rows with `reportedAt = now`, so a stored bucket
 * would read "Menos de 1 semana" forever. The contract therefore carries a
 * calendar day, and this module owns both directions: {@link sinceDayOf} picks
 * the day a bucket stands for, {@link sinceBucketOf} picks the bucket a stored
 * day now falls in. They are deliberately NOT inverses — a pain saved "Há 1
 * mês" reads "Há 2 meses" a month later, which is the point of storing a day.
 *
 * The draft therefore holds the DAY, not the bucket, and passes it through
 * untouched when the person does not touch the question. Holding the bucket
 * instead would push the day forward on every unrelated re-save (an intensity
 * correction two months on would move the start of the pain by two months).
 */

export const PAIN_AREAS = [
  "cervical",
  "thoracic",
  "lumbar",
  "chest",
  "abdomen",
  "shoulder",
  "elbow",
  "wrist",
  "hip",
  "knee",
  "ankle",
] as const;

export type PainArea = (typeof PAIN_AREAS)[number];

const PAIRED_AREAS = new Set<PainArea>([
  "shoulder",
  "elbow",
  "wrist",
  "hip",
  "knee",
  "ankle",
]);

export type PainSide = "both" | "left" | "right";

export type InjuryNature = (typeof INJURY_NATURES)[number];
export type InjuryMoment = (typeof INJURY_MOMENTS)[number];
export type InjuryType = (typeof INJURY_TYPES)[number];
export type InjuryStatus = (typeof INJURY_STATUSES)[number];
export type InjuryAge = (typeof INJURY_AGES)[number];

export function isPaired(area: PainArea): boolean {
  return PAIRED_AREAS.has(area);
}

/** A stored region back into A2's area plus side. */
export function areaOf(region: BodyRegion): {
  readonly area: PainArea;
  readonly side: PainSide | null;
} {
  if (region.endsWith("_r")) {
    return { area: region.slice(0, -2) as PainArea, side: "right" };
  }
  if (region.endsWith("_l")) {
    return { area: region.slice(0, -2) as PainArea, side: "left" };
  }
  return { area: region as PainArea, side: null };
}

/** An area plus a side as the contract's regions: one, or two for "both". */
export function regionsOf(area: PainArea, side: PainSide): BodyRegion[] {
  if (!isPaired(area)) {
    return [area as BodyRegion];
  }
  if (side === "both") {
    return [`${area}_r` as BodyRegion, `${area}_l` as BodyRegion];
  }
  return [`${area}_${side === "right" ? "r" : "l"}` as BodyRegion];
}

/** A2's default intensity for a new pain. */
export const DEFAULT_INTENSITY = 3;

/** A2's four "Desde quando?" answers (editors.tsx:1015-1022). */
export const SINCE_BUCKETS = [
  "week",
  "month",
  "two_months",
  "half_year",
] as const;

export type SinceBucket = (typeof SINCE_BUCKETS)[number];

/** The day each bucket stands for, as an age in days on the day it is picked. */
const BUCKET_AGE: Readonly<Record<SinceBucket, number>> = {
  half_year: 183,
  month: 30,
  two_months: 60,
  week: 3,
};

/**
 * Where a stored day falls today. The edges sit between the buckets' ages, so
 * a pain saved "Há 1 mês" (30 days) still reads "Há 1 mês" a fortnight later
 * and only then becomes "Há 2 meses".
 */
const BUCKET_EDGE: readonly (readonly [number, SinceBucket])[] = [
  [14, "week"],
  [45, "month"],
  [120, "two_months"],
];

const MS_PER_DAY = 86_400_000;

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

const dayString = (date: Date): string =>
  `${String(date.getFullYear()).padStart(4, "0")}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

/** Local midnight of `date`'s own calendar day — never `Date.UTC`, because
 *  "today" here is the day the person's phone is showing. */
const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * A `YYYY-MM-DD` as a local date, or `null` when the text is not a day that
 * exists. The round trip is the check: `new Date(2026, 1, 31)` is March 3rd
 * and never throws, so the regex alone would let `2026-02-31` through (T62
 * found the same thing server-side).
 */
function parseDay(day: string): Date | null {
  const parts = CALENDAR_DAY.exec(day);
  if (parts === null) {
    return null;
  }
  const date = new Date(
    Number(parts[1]),
    Number(parts[2]) - 1,
    Number(parts[3])
  );
  return dayString(date) === day ? date : null;
}

/** The day a bucket stands for, on `now`'s local calendar. */
export function sinceDayOf(bucket: SinceBucket, now: Date): string {
  const today = startOfDay(now);
  return dayString(
    new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() - BUCKET_AGE[bucket]
    )
  );
}

/**
 * The bucket a stored day falls in on `now`'s local calendar, or `null` when
 * the day is malformed. A day in the future reads as the nearest bucket; the
 * server refuses one anyway (`injury.since_in_future`).
 */
export function sinceBucketOf(day: string, now: Date): SinceBucket | null {
  const start = parseDay(day);
  if (start === null) {
    return null;
  }
  // Rounded, so a DST day of 23 or 25 hours does not shift the count.
  const elapsed = Math.round(
    (startOfDay(now).getTime() - start.getTime()) / MS_PER_DAY
  );
  return BUCKET_EDGE.find(([edge]) => elapsed < edge)?.[1] ?? "half_year";
}

export interface PainForm {
  readonly area: PainArea | null;
  /** The draft came from a STORED report, not the "add" button. It decides two
   *  things A2 decided from the same fact: whether "Como está evoluindo?" is
   *  asked at all (editors.tsx:1046), and whether "Desde quando?" is required
   *  — an old row saved before either question existed must stay savable. */
  readonly existing: boolean;
  readonly howLongAgo: InjuryAge | null;
  readonly intensity: number;
  readonly nature: InjuryNature | null;
  readonly note: string;
  readonly occurrence: InjuryOccurrence;
  readonly side: PainSide;
  /** The contract's `since`, a `YYYY-MM-DD` — the bucket is what the form
   *  draws from it, so an untouched question re-saves the same day. */
  readonly sinceDay: string | null;
  readonly status: InjuryStatus | null;
  readonly trend: PainTrend | null;
  readonly type: InjuryType | null;
  readonly when: InjuryMoment | null;
}

export function emptyPainForm(): PainForm {
  return {
    area: null,
    existing: false,
    howLongAgo: null,
    intensity: DEFAULT_INTENSITY,
    nature: null,
    note: "",
    occurrence: "current",
    side: "right",
    sinceDay: null,
    status: null,
    trend: null,
    type: null,
    when: null,
  };
}

/**
 * A stored report as the editor's draft.
 *
 * `trend` falls back to `same` on a CURRENT report, which is A2's default for
 * the control it is about to draw (editors.tsx:942) — the person sees "Igual"
 * selected, so "Igual" is what saving means. A new pain never sees the control
 * and keeps `null`, so "nothing said" stays a state the card can fall back on.
 */
export function painFormFrom(injury: InjuryInput): PainForm {
  const { area, side } = areaOf(injury.region);
  const current = injury.occurrence === "current";
  return {
    area,
    existing: true,
    howLongAgo: injury.howLongAgo,
    intensity: injury.intensity ?? DEFAULT_INTENSITY,
    nature: injury.nature,
    note: injury.note ?? "",
    occurrence: injury.occurrence,
    side: side ?? "right",
    sinceDay: injury.since ?? null,
    status: injury.status,
    trend: current ? (injury.trend ?? "same") : null,
    type: injury.type,
    when: injury.when,
  };
}

/**
 * The questions the chosen occurrence still needs an answer to. Checked
 * before the contract so each one is a field error under its own control;
 * the contract's refinement names no path and could only say "something".
 */
export function painFormErrors(form: PainForm): FieldErrors {
  const errors: Record<string, string> = {};
  if (form.area === null) {
    errors.region = "required";
  }
  const branch =
    form.occurrence === "current"
      ? { nature: form.nature, when: form.when }
      : { howLongAgo: form.howLongAgo, status: form.status, type: form.type };
  for (const [field, value] of Object.entries(branch)) {
    if (value === null) {
      errors[field] = "required";
    }
  }
  // Required of a NEW pain, as A2 required it (editors.tsx:950), and optional
  // on a stored row that predates the question — refusing to save that row
  // would strand every pain logged before this shipped.
  if (
    form.occurrence === "current" &&
    !form.existing &&
    form.sinceDay === null
  ) {
    errors.since = "required";
  }
  return Object.keys(errors).length === 0 ? NO_ERRORS : errors;
}

/**
 * The reports the draft stands for — one, or two for "both" sides — with the
 * other occurrence's fields `null`. The note is trimmed; empty is `null`.
 * Unvalidated: a missing answer stays `null` and the contract refuses it.
 *
 * `since` and `trend` are written ONLY when the draft has one, never as an
 * explicit `null`: the two keys are new to every server older than T62, and
 * `injuryListInput` is strict, so a body that always carries them is a 422 the
 * outbox quarantines. Absent and `null` mean the same thing to the server, so
 * the app gives up nothing by leaving them out. A historical report never
 * carries either — the contract's third refine refuses that outright.
 */
export function injuriesFromPainForm(form: PainForm): InjuryInput[] {
  if (form.area === null) {
    return [];
  }
  const note = form.note.trim() === "" ? null : form.note.trim();
  const current = form.occurrence === "current";
  const since = current && form.sinceDay !== null ? form.sinceDay : null;
  const trend = current ? form.trend : null;
  return regionsOf(form.area, form.side).map((region) => ({
    howLongAgo: current ? null : form.howLongAgo,
    intensity: current ? form.intensity : null,
    nature: current ? form.nature : null,
    note,
    occurrence: form.occurrence,
    region,
    status: current ? null : form.status,
    type: current ? null : form.type,
    when: current ? form.when : null,
    ...(since === null ? {} : { since }),
    ...(trend === null ? {} : { trend }),
  }));
}

const sameReport = (a: InjuryInput, b: InjuryInput) =>
  a.region === b.region && a.occurrence === b.occurrence;

/**
 * The WHOLE list the `PUT` carries after editing the report at `index` (or
 * adding one, `index` `null`).
 *
 * `PUT /me/injuries` replaces the list, so every other stored report rides
 * along unchanged — dropping one here is deleting it with no trace. The one
 * exception: a stored report on the same region AND occurrence as a new one is
 * superseded by it, not duplicated (marking "both" shoulders while the left
 * one is already listed).
 */
export function applyPainEdit(
  stored: readonly InjuryInput[],
  index: number | null,
  form: PainForm
): { readonly injuries: InjuryInput[] } {
  const next = injuriesFromPainForm(form);
  const inPlace = index !== null && index >= 0 && index < stored.length;
  const injuries: InjuryInput[] = [];
  for (const [i, row] of stored.entries()) {
    if (i === index) {
      // The edited report stays where it was, so the list does not reshuffle.
      injuries.push(...next);
    } else if (!next.some((n) => sameReport(n, row))) {
      injuries.push(row);
    }
  }
  return { injuries: inPlace ? injuries : [...injuries, ...next] };
}

/** The whole list without the report at `index` ("Não dói mais"). */
export function removePain(
  stored: readonly InjuryInput[],
  index: number
): { readonly injuries: InjuryInput[] } {
  return { injuries: stored.filter((_, i) => i !== index) };
}

/** A2's `intensityWord` (:939), as a key the form localizes. */
export function intensityLevel(n: number): "mild" | "moderate" | "strong" {
  if (n <= 3) {
    return "mild";
  }
  return n <= 6 ? "moderate" : "strong";
}
